/**
 * ImageReloader
 * 图片轮询重载工具类（v2.0 混合轮询版）
 *
 * 设计原则：
 * - 对调用方隐藏内部细节，统一 pendingMap 管理所有待轮询图片。
 * - 混合轮询：每 5s 用 new Image() 快速尝试；每 30s 用 GET /imgcenter/restore_status 批量查状态兜底。
 * - 通过 restore_status 快速识别 standard / restore_readable / failed，避免 0kb / 不可恢复图片被 onerror 无限卡住。
 * - 最大轮询时间 10 分钟，超时后统一按 skip 处理（兼容 Mainloading 阻塞式流程不卡死）。
 * - 资源清理：destroy 时立即停止轮询、清理所有 Image 对象和定时器。
 */

const DEFAULT_POLL_INTERVAL_MS = 5000;
const DEFAULT_STATUS_POLL_INTERVAL_MS = 30000;
const DEFAULT_MAX_POLL_TIME_MS = 10 * 60 * 1000;
const DEFAULT_LOAD_TIMEOUT_MS = 5000;
const DEFAULT_POLL_PROGRESS_MAX_PENDING = 0.95;

function getCleanUrl(url) {
    if (typeof url !== 'string') return '';
    return url.split('?')[0];
}

class ImageReloader {
    constructor(options = {}) {
        this.pollInterval = options.pollInterval || DEFAULT_POLL_INTERVAL_MS;
        this.statusPollInterval = options.statusPollInterval || DEFAULT_STATUS_POLL_INTERVAL_MS;
        this.maxPollTime = options.maxPollTime || DEFAULT_MAX_POLL_TIME_MS;
        this.loadTimeout = options.loadTimeout || DEFAULT_LOAD_TIMEOUT_MS;
        this.pollProgressMaxPending = typeof options.pollProgressMaxPending === 'number'
            ? options.pollProgressMaxPending
            : DEFAULT_POLL_PROGRESS_MAX_PENDING;

        // 与 Mainloading 进度计算保持兼容：允许外部传入 maxPollAttempts，否则按时间换算
        this.maxPollAttempts = typeof options.maxPollAttempts === 'number' && options.maxPollAttempts > 0
            ? options.maxPollAttempts
            : Math.floor(this.maxPollTime / this.pollInterval);
        this.statusCheckEveryNAttempts = options.statusCheckEveryNAttempts
            || Math.floor(this.statusPollInterval / this.pollInterval)
            || 6;

        this.isDestroyed = false;
        this.pollDelayTimer = null;
        this.pollDelayResolve = null;
        this.pollActiveImages = new Set();
    }

    /**
     * 批量轮询重载
     * @param {Array} errorImages - 加载失败的图片列表，每项包含 { image, imageObject, url, index }
     * @param {Object} options
     * @param {Function} options.onItemSuccess(item, imageObject) - 单张成功回调
     * @param {Function} options.onItemError(item) - 单张失败/跳过回调
     * @param {Function} options.onAttempt(item, attempts, isSuccess, timedOut, reason) - 单张图有结果时回调
     * @param {Function} options.onRound(attempts) - 每轮轮询开始回调（用于全局进度）
     * @param {Function} options.onEnd(preheatResults) - 全部完成/超时后回调
     * @param {Function} options.onError(err) - 轮询循环内部异常回调
     * @param {Function} options.queryRestoreStatus(items) - 查询解冻状态，返回 Promise<{ results: [{url, status, readable}] }>
     * @returns {Promise<Array<{index, url, status, checkedAt}>>} - 预热结果数组
     */
    async reloadBatch(errorImages, options = {}) {
        const { onItemSuccess, onItemError, onAttempt, onRound, onEnd, onError, queryRestoreStatus } = options;

        const stateMap = new Map();
        const resolvedStates = [];

        (errorImages || []).forEach((item) => {
            const key = getCleanUrl(item.url);
            if (!key) return;
            stateMap.set(key, {
                item,
                key,
                attempts: 0,
                lastStatus: '',
                imageObject: null,
                successReason: '', // 'image_load' | 'restore_status_standard' | 'restore_status_readable'
                outcome: null // 'success' | 'permanent_failed' | 'timeout'
            });
        });

        const startTime = Date.now();
        let globalAttempts = 0;

        const resolveState = (state, outcome, imageObject) => {
            if (state.outcome) return;
            state.outcome = outcome;
            state.imageObject = imageObject || state.imageObject;
            stateMap.delete(state.key);
            resolvedStates.push(state);

            if (outcome === 'success') {
                const finalImageObject = state.imageObject || { width: 300, height: 300 };
                state.item.image['orisize'] = [finalImageObject.width, finalImageObject.height];
                if (typeof onItemSuccess === 'function') {
                    onItemSuccess(state.item, finalImageObject);
                }
                if (typeof onAttempt === 'function') {
                    const reason = state.successReason || 'image_load';
                    onAttempt(state.item, state.attempts, true, false, reason);
                }
            } else {
                state.item.image['orisize'] = [300, 300];
                if (typeof onItemError === 'function') {
                    onItemError(state.item);
                }
                if (typeof onAttempt === 'function') {
                    onAttempt(state.item, state.attempts, false, true, outcome);
                }
            }
        };

        try {
            while (stateMap.size > 0 && !this.isDestroyed && (Date.now() - startTime) < this.maxPollTime) {
                globalAttempts++;

                // eslint-disable-next-line no-console
                console.log('[DEBUG ImageReloader round]', { globalAttempts, pendingCount: stateMap.size, doStatusCheck: globalAttempts % this.statusCheckEveryNAttempts === 0, pendingUrls: Array.from(stateMap.values()).map(s => s.item.url) });

                // 每轮全局进度回调：无论本轮是否有图出结果，都保证触发
                if (typeof onRound === 'function') {
                    onRound(globalAttempts);
                }

                const doStatusCheck = globalAttempts % this.statusCheckEveryNAttempts === 0;

                // 每张 pending 图片的尝试次数 +1，用于平滑进度
                stateMap.forEach((state) => {
                    state.attempts++;
                });

                const pollResult = await this._pollOnce(
                    Array.from(stateMap.values()),
                    doStatusCheck,
                    queryRestoreStatus
                );

                // 处理成功
                pollResult.success.forEach((state) => {
                    resolveState(state, 'success', state.imageObject);
                });

                // 处理永久失败（restore_status 明确 failed）
                pollResult.failed.forEach((state) => {
                    resolveState(state, 'permanent_failed', null);
                });

                if (stateMap.size > 0) {
                    await this._delay(this.pollInterval);
                }
            }
        } catch (err) {
            // 内部异常不应阻断流程，剩余 pending 按超时处理
            // 但必须暴露出来，否则出现 bug 时无法排查
            if (typeof onError === 'function') {
                onError(err);
            } else {
                // eslint-disable-next-line no-console
                console.error('[ImageReloader] poll loop error:', err);
            }
        }

        // 循环结束：剩余未完成的统一按超时处理
        Array.from(stateMap.values()).forEach((state) => {
            resolveState(state, 'timeout', null);
        });

        const checkedAt = Date.now();
        // 保持与旧版 Mainloading preheatResults 兼容：status 用数值 200/408/403
        const outcomeStatusMap = {
            success: 200,
            timeout: 408,
            permanent_failed: 403
        };
        const results = (errorImages || []).map((item) => {
            const key = getCleanUrl(item.url);
            const state = resolvedStates.find((s) => s.key === key);
            return {
                index: typeof item.index === 'number' ? item.index : -1,
                url: item.url,
                status: state ? (outcomeStatusMap[state.outcome] || 403) : 408,
                checkedAt
            };
        });

        if (typeof onEnd === 'function') {
            onEnd(results);
        }

        return results;
    }

    /**
     * 单次混合轮询
     * @param {Array} pendingStates - pending 状态数组
     * @param {boolean} doStatusCheck - 是否查询 restore_status
     * @param {Function} queryRestoreStatus - 状态查询函数
     * @returns {Object} { success: [], failed: [] }
     */
    async _pollOnce(pendingStates, doStatusCheck, queryRestoreStatus) {
        const result = { success: [], failed: [] };

        // 1. Image 快速路径
        const imageResults = await Promise.all(
            pendingStates.map((state) => this._tryLoadImage(state))
        );

        imageResults.forEach(({ state, loaded, imageObject }) => {
            if (loaded) {
                state.imageObject = imageObject;
                result.success.push(state);
            }
        });

        const remaining = pendingStates.filter((state) => !result.success.includes(state));

        // 2. restore_status 兜底
        if (doStatusCheck && remaining.length > 0 && typeof queryRestoreStatus === 'function') {
            try {
                const { results: statusResults } = await queryRestoreStatus(
                    remaining.map((state) => state.item)
                );
                const statusMap = new Map();
                (statusResults || []).forEach((r) => {
                    if (r && r.url) statusMap.set(getCleanUrl(r.url), r);
                });

                // eslint-disable-next-line no-console
                console.log('[DEBUG ImageReloader restore_status]', { statusResultsCount: (statusResults || []).length, statusMapEntries: Array.from(statusMap.entries()).map(([k, v]) => ({ key: k, status: v.status, readable: v.readable })) });

                for (const state of remaining) {
                    const status = statusMap.get(getCleanUrl(state.item.url));
                    if (!status) continue;

                    state.lastStatus = status.status;

                    if (status.status === 'standard') {
                        state.successReason = 'restore_status_standard';
                        // restore_status 已确认图片可读，立即实际加载一次：
                        // 1. 让浏览器缓存该图片，避免 Exhi 渲染时重新拉取
                        // 2. 获取真实宽高写入 orisize
                        const loadResult = await this._tryLoadImage(state);
                        if (loadResult.loaded) {
                            state.imageObject = loadResult.imageObject;
                        }
                        result.success.push(state);
                    } else if (status.status === 'restore_readable' && status.readable) {
                        state.successReason = 'restore_status_readable';
                        const loadResult = await this._tryLoadImage(state);
                        if (loadResult.loaded) {
                            state.imageObject = loadResult.imageObject;
                        }
                        result.success.push(state);
                    } else if (status.status === 'failed') {
                        result.failed.push(state);
                    }
                    // cold / restore_ongoing：继续 pending
                }
            } catch (err) {
                // restore_status 查询失败不影响 Image 轮询，忽略
            }
        }

        return result;
    }

    /**
     * 单次 Image 加载尝试，带超时保护
     */
    _tryLoadImage(state) {
        return new Promise((resolve) => {
            const im = new Image();
            let completed = false;
            let timeoutId = null;

            this.pollActiveImages.add(im);

            const finish = (loaded, imageObject) => {
                if (completed) return;
                completed = true;

                if (timeoutId) {
                    clearTimeout(timeoutId);
                    timeoutId = null;
                }

                im.onload = null;
                im.onerror = null;
                im.src = '';
                this.pollActiveImages.delete(im);

                resolve({ state, loaded, imageObject });
            };

            timeoutId = setTimeout(() => {
                finish(false, null);
            }, this.loadTimeout);

            im.onload = function () {
                finish(true, { width: this.width, height: this.height });
            };

            im.onerror = () => {
                finish(false, null);
            };

            try {
                im.src = state.item.url;
            } catch (err) {
                finish(false, null);
            }
        });
    }

    _delay(ms) {
        return new Promise((resolve) => {
            this.pollDelayResolve = resolve;
            this.pollDelayTimer = setTimeout(() => {
                this.pollDelayTimer = null;
                this.pollDelayResolve = null;
                resolve();
            }, ms);
        });
    }

    destroy() {
        this.isDestroyed = true;

        this.pollActiveImages.forEach((im) => {
            if (im) {
                im.onload = null;
                im.onerror = null;
                im.src = '';
            }
        });
        this.pollActiveImages.clear();

        if (this.pollDelayTimer) {
            clearTimeout(this.pollDelayTimer);
            this.pollDelayTimer = null;
        }
        if (this.pollDelayResolve) {
            this.pollDelayResolve();
            this.pollDelayResolve = null;
        }
    }
}

export default ImageReloader;
